import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';

const GOAL_TYPES = [
  { value: 'vacation', label: 'Vacation', icon: 'Plane', color: '#00E5FF' },
  { value: 'emergency_fund', label: 'Emergency Fund', icon: 'Shield', color: '#7B2FBE' },
  { value: 'debt_repayment', label: 'Debt Repayment', icon: 'CreditCard', color: '#F59E0B' },
  { value: 'home_down_payment', label: 'Home Down Payment', icon: 'Home', color: '#10B981' },
  { value: 'custom', label: 'Custom', icon: 'Target', color: '#06B6D4' },
];

export default function AddGoalModal({ isOpen, onClose, onSave, editGoal }) {
  const [form, setForm] = useState({
    title: '',
    type: 'custom',
    target_amount: '',
    current_amount: '',
    deadline: '',
    monthly_contribution: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (editGoal) {
      setForm({
        title: editGoal?.title || '',
        type: editGoal?.type || 'custom',
        target_amount: editGoal?.target_amount || '',
        current_amount: editGoal?.current_amount || '',
        deadline: editGoal?.deadline || '',
        monthly_contribution: editGoal?.monthly_contribution || '',
      });
    } else {
      setForm({ title: '', type: 'custom', target_amount: '', current_amount: '', deadline: '', monthly_contribution: '' });
    }
    setError('');
  }, [editGoal, isOpen]);

  const selectedType = GOAL_TYPES?.find(t => t?.value === form?.type) || GOAL_TYPES?.[4];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.title?.trim()) { setError('Goal name is required'); return; }
    if (!form?.target_amount || parseFloat(form?.target_amount) <= 0) { setError('Target amount must be greater than 0'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({
        ...form,
        icon: selectedType?.icon,
        color: selectedType?.color,
        target_amount: parseFloat(form?.target_amount),
        current_amount: parseFloat(form?.current_amount) || 0,
        monthly_contribution: parseFloat(form?.monthly_contribution) || 0,
        deadline: form?.deadline || null,
      });
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save goal');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#0F172A] border border-[rgba(6,182,212,0.2)] rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[rgba(6,182,212,0.1)]">
          <div>
            <h2 className="font-heading font-bold text-lg text-[#F8FAFC]">
              {editGoal ? 'Edit Goal' : 'Add New Goal'}
            </h2>
            <p className="text-xs text-[#64748B] mt-0.5">Set your financial target and track progress</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[rgba(6,182,212,0.1)] transition-colors">
            <Icon name="X" size={16} color="#64748B" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Goal Type */}
          <div>
            <label className="block text-xs font-medium text-[#94A3B8] mb-2">Goal Type</label>
            <div className="grid grid-cols-5 gap-2">
              {GOAL_TYPES?.map(type => (
                <button
                  key={type?.value}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, type: type?.value }))}
                  className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl border transition-all duration-200"
                  style={{
                    borderColor: form?.type === type?.value ? type?.color : 'rgba(6,182,212,0.1)',
                    backgroundColor: form?.type === type?.value ? `${type?.color}15` : 'transparent',
                  }}
                >
                  <Icon name={type?.icon} size={18} color={form?.type === type?.value ? type?.color : '#64748B'} />
                  <span className="text-[9px] font-medium text-center leading-tight" style={{ color: form?.type === type?.value ? type?.color : '#64748B' }}>
                    {type?.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Goal Name */}
          <div>
            <label className="block text-xs font-medium text-[#94A3B8] mb-1.5">Goal Name *</label>
            <input
              type="text"
              value={form?.title}
              onChange={e => setForm(f => ({ ...f, title: e?.target?.value }))}
              placeholder="e.g. Europe Trip 2026"
              className="w-full bg-[#0A1628] border border-[rgba(6,182,212,0.15)] rounded-lg px-3 py-2.5 text-sm text-[#F8FAFC] placeholder-[#475569] focus:outline-none focus:border-[#06B6D4] transition-colors"
            />
          </div>

          {/* Amounts */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-1.5">Target Amount *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B] text-sm">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form?.target_amount}
                  onChange={e => setForm(f => ({ ...f, target_amount: e?.target?.value }))}
                  placeholder="5,000"
                  className="w-full bg-[#0A1628] border border-[rgba(6,182,212,0.15)] rounded-lg pl-7 pr-3 py-2.5 text-sm text-[#F8FAFC] placeholder-[#475569] focus:outline-none focus:border-[#06B6D4] transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-1.5">Initial Deposit</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B] text-sm">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form?.current_amount}
                  onChange={e => setForm(f => ({ ...f, current_amount: e?.target?.value }))}
                  placeholder="0"
                  className="w-full bg-[#0A1628] border border-[rgba(6,182,212,0.15)] rounded-lg pl-7 pr-3 py-2.5 text-sm text-[#F8FAFC] placeholder-[#475569] focus:outline-none focus:border-[#06B6D4] transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Deadline & Monthly */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-1.5">Target Deadline</label>
              <input
                type="date"
                value={form?.deadline}
                onChange={e => setForm(f => ({ ...f, deadline: e?.target?.value }))}
                className="w-full bg-[#0A1628] border border-[rgba(6,182,212,0.15)] rounded-lg px-3 py-2.5 text-sm text-[#F8FAFC] focus:outline-none focus:border-[#06B6D4] transition-colors [color-scheme:dark]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-1.5">Monthly Contribution</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B] text-sm">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form?.monthly_contribution}
                  onChange={e => setForm(f => ({ ...f, monthly_contribution: e?.target?.value }))}
                  placeholder="200"
                  className="w-full bg-[#0A1628] border border-[rgba(6,182,212,0.15)] rounded-lg pl-7 pr-3 py-2.5 text-sm text-[#F8FAFC] placeholder-[#475569] focus:outline-none focus:border-[#06B6D4] transition-colors"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.2)] rounded-lg">
              <Icon name="AlertCircle" size={14} color="#EF4444" />
              <p className="text-xs text-[#EF4444]">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg border border-[rgba(6,182,212,0.2)] text-sm text-[#94A3B8] hover:bg-[rgba(6,182,212,0.05)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-[#0F172A] transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #00E5FF, #06B6D4)' }}
            >
              {saving ? (
                <><div className="w-4 h-4 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin" /> Saving...</>
              ) : (
                <>{editGoal ? 'Update Goal' : 'Create Goal'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
